import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNotifications } from "../../../../context/NotificationContext";
import { useWaitlistApi } from "../../../../hooks/useWaitlistApi";
import {
  latestSyncSeq,
  takeNewSyncEvents,
} from "../../../../utils/syncEventCursor";
import type {
  AssignWaitlistEntryRequest,
  AssignWaitlistEntryResponse,
  CreateWaitlistEntryRequest,
  UpdateWaitlistEntryRequest,
  WaitlistEntry,
} from "../../../../types/waitlist";
import { orderEntries, upsertEntry } from "./waitlistModel";

/** Burst window for waitlist.sync refetches (one GET per burst). */
export const WAITLIST_SYNC_DEBOUNCE_MS = 300;

interface UseWaitlistArgs {
  /** False: no fetch (no business location / no permission). */
  enabled: boolean;
}

/**
 * The walk-in waitlist store, owned by CalendarView so the header count stays
 * live while the drawer is closed. Loads today's rows on mount (and on a
 * location change), refetches on `waitlist.sync` (debounced), and applies
 * this client's own writes locally (their Pusher echo is suppressed).
 *
 * Sync cursor: the hook fetches the current list when it mounts, so it
 * lazy-inits the cursor to the latest seq instead of replaying the backlog.
 */
export function useWaitlist({ enabled }: UseWaitlistArgs) {
  const api = useWaitlistApi();
  const { listWaitlist } = api;
  const { waitlistSyncEvents } = useNotifications();
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loaded, setLoaded] = useState(false);
  // Shadow of `entries` for rollback reads outside render.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  // Bumped by every fetch and local write: a GET that resolves after a newer
  // write (or a newer GET) is dropped.
  const versionRef = useRef(0);

  const commit = useCallback((next: WaitlistEntry[]) => {
    versionRef.current += 1;
    entriesRef.current = next;
    setEntries(next);
  }, []);

  const refetch = useCallback(async () => {
    versionRef.current += 1;
    const version = versionRef.current;
    try {
      const rows = await listWaitlist();
      if (version !== versionRef.current) return;
      entriesRef.current = rows;
      setEntries(rows);
    } catch (error) {
      console.error("Error loading the walk-in waitlist:", error);
    } finally {
      if (version === versionRef.current) setLoaded(true);
    }
  }, [listWaitlist]);

  // Initial load and location switch (listWaitlist changes with the location).
  useEffect(() => {
    if (!enabled) {
      versionRef.current += 1;
      entriesRef.current = [];
      setEntries([]);
      setLoaded(false);
      return;
    }
    void refetch();
  }, [enabled, refetch]);

  // waitlist.sync → one debounced refetch per burst.
  const cursorRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    if (cursorRef.current === null) {
      cursorRef.current = latestSyncSeq(waitlistSyncEvents);
      return;
    }
    const { newEvents, lastSeenSeq } = takeNewSyncEvents(
      waitlistSyncEvents,
      cursorRef.current,
    );
    if (newEvents.length === 0) return;
    cursorRef.current = lastSeenSeq;
    if (!enabled) return;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void refetchRef.current();
    }, WAITLIST_SYNC_DEBOUNCE_MS);
  }, [waitlistSyncEvents, enabled]);
  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const {
    createEntry,
    updateEntry,
    removeEntry,
    reorderWaitlist,
    assignEntry,
  } = api;

  /** Add at the end of the line; resolves with the row and its 1-based place. */
  const create = useCallback(
    async (data: CreateWaitlistEntryRequest) => {
      const entry = await createEntry(data);
      const next = upsertEntry(entriesRef.current, entry);
      commit(next);
      return {
        entry,
        position: next.findIndex((e) => e.uuid === entry.uuid) + 1,
      };
    },
    [createEntry, commit],
  );

  const update = useCallback(
    async (uuid: string, data: UpdateWaitlistEntryRequest) => {
      const entry = await updateEntry(uuid, data);
      commit(upsertEntry(entriesRef.current, entry));
      return entry;
    },
    [updateEntry, commit],
  );

  const remove = useCallback(
    async (uuid: string) => {
      await removeEntry(uuid);
      commit(entriesRef.current.filter((e) => e.uuid !== uuid));
    },
    [removeEntry, commit],
  );

  /** Optimistic: the new order shows at once and rolls back on failure. */
  const reorder = useCallback(
    async (uuids: string[]) => {
      const before = entriesRef.current;
      commit(orderEntries(before, uuids));
      try {
        const rows = await reorderWaitlist(uuids);
        if (rows.length) commit(rows);
      } catch (error) {
        commit(before);
        throw error;
      }
    },
    [reorderWaitlist, commit],
  );

  /** Books the walk-in; the row leaves the list. */
  const assign = useCallback(
    async (
      uuid: string,
      data: AssignWaitlistEntryRequest,
    ): Promise<AssignWaitlistEntryResponse> => {
      const response = await assignEntry(uuid, data);
      commit(entriesRef.current.filter((e) => e.uuid !== uuid));
      return response;
    },
    [assignEntry, commit],
  );

  return useMemo(
    () => ({
      entries,
      loaded,
      count: entries.length,
      refetch,
      create,
      update,
      remove,
      reorder,
      assign,
    }),
    [entries, loaded, refetch, create, update, remove, reorder, assign],
  );
}

export type WaitlistStore = ReturnType<typeof useWaitlist>;
